import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "./redrafter.module.scss";

type RedrafterOrchestratorAgentProps = {
  parsedContent: any;
};

// Helper function to get severity color
const getSeverityColor = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case "high":
      return { bg: "#fee2e2", text: "#991b1b" };
    case "medium":
      return { bg: "#fef9c3", text: "#854d0e" };
    case "low":
      return { bg: "#dcfce7", text: "#166534" };
    default:
      return { bg: "#f3f4f6", text: "#374151" };
  }
};

// Accordion Item Component
const AccordionItem: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className={styles.accordionItem}>
      <div className={styles.accordionHeader} onClick={onToggle}>
        <h4 className={styles.accordionTitle}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          {title}
        </h4>
      </div>
      {isOpen && <div className={styles.accordionContent}>{children}</div>}
    </div>
  );
};

export default function RedrafterOrchestratorAgent({
  parsedContent,
}: RedrafterOrchestratorAgentProps): JSX.Element {
  const data = parsedContent?.finalAnswer || parsedContent || {};
  const result = data?.result || data;

  // Preprocess redrafted contract to replace escape sequences with actual characters
  const rawContract = result?.redrafted_contract;
  const redraftedContract = rawContract
    ? rawContract
        .replace(/\\n/g, "\n")   // Replace literal \n with newline
        .replace(/\\t/g, "\t")   // Replace literal \t with tab
        .replace(/\\r/g, "\r")   // Replace literal \r with carriage return
    : rawContract;

  // Preprocess negotiation points to replace escape sequences
  const rawNegotiationPoints = result?.negotiation_points;
  const negotiationPoints = rawNegotiationPoints?.map((point: any) => ({
    ...point,
    original_text: point.original_text?.replace(/\\n/g, "\n").replace(/\\t/g, "\t"),
    updated_text: point.updated_text?.replace(/\\n/g, "\n").replace(/\\t/g, "\t"),
    negotiation_point: point.negotiation_point?.replace(/\\n/g, "\n").replace(/\\t/g, "\t"),
  }));

  const remainingRisks = result?.remaining_risks_summary;

  const hasAnyContent =
    !!redraftedContract ||
    (negotiationPoints && negotiationPoints.length) ||
    (remainingRisks && remainingRisks.length);

  // Accordion state
  const [openSections, setOpenSections] = useState({
    changeSummary: true,
    redraftedContract: true,
    remainingRisks: true,
  });

  // State for contract subsections (all sections closed by default)
  const [openContractSections, setOpenContractSections] = useState<{
    [key: string]: boolean;
  }>({});

  // State for change summary items (all items closed by default)
  const [openChangeItems, setOpenChangeItems] = useState<{
    [key: number]: boolean;
  }>({});

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleContractSection = (sectionKey: string) => {
    setOpenContractSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleChangeItem = (index: number) => {
    setOpenChangeItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Parse contract sections
  const parseContractSections = (markdown: string) => {
    if (!markdown) return [];

    const sections: Array<{ title: string; content: string; key: string }> = [];
    const lines = markdown.split("\n");
    let currentSection: { title: string; content: string; key: string } | null = null;

    lines.forEach((line) => {
      // Match main section headers like "1. SECTION NAME" or "## 1. SECTION NAME"
      const mainSectionMatch = line.match(/^#{0,2}\s*(\d+)\.\s+([A-Z][A-Z\s,&\-]+)$/);

      if (mainSectionMatch) {
        // Save previous section if exists
        if (currentSection) {
          sections.push(currentSection);
        }

        // Start new section
        const sectionNumber = mainSectionMatch[1];
        const sectionTitle = mainSectionMatch[2].trim();
        currentSection = {
          title: `${sectionNumber}. ${sectionTitle}`,
          content: "",
          key: `section-${sectionNumber}`,
        };
      } else if (currentSection) {
        // Add line to current section content
        currentSection.content += line + "\n";
      } else {
        // Content before first section (header/intro)
        if (!sections.length || sections[0].key !== "intro") {
          sections.unshift({
            title: "Introduction",
            content: "",
            key: "intro",
          });
        }
        if (sections[0]) {
          sections[0].content += line + "\n";
        }
      }
    });

    // Don't forget the last section
    if (currentSection) {
      sections.push(currentSection);
    }

    return sections.filter((s) => s.content.trim());
  };

  const contractSections = redraftedContract ? parseContractSections(redraftedContract) : [];

  // Expand/Collapse all contract sections
  const expandAllContractSections = () => {
    const allOpen: { [key: string]: boolean } = {};
    contractSections.forEach((section) => {
      allOpen[section.key] = true;
    });
    setOpenContractSections(allOpen);
  };

  const collapseAllContractSections = () => {
    setOpenContractSections({});
  };

  if (!hasAnyContent) {
    return (
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: "#bd1919",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          No redrafted contract or additional information available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          marginBottom: "20px",
          padding: "16px 20px",
          background: "#dcfce7",
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "#004141",
            marginBottom: "8px",
          }}
        >
          Redrafted Contract
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "#267103",
            margin: 0,
          }}
        >
          Below is the redrafted contract with all revisions and recommendations
          applied.
        </Typography>
      </Box>

      {/* Negotiation Points Accordion */}
      {negotiationPoints && negotiationPoints.length > 0 && (
        <AccordionItem
          title={`Change Summary (${negotiationPoints.length})`}
          isOpen={openSections.changeSummary}
          onToggle={() => toggleSection("changeSummary")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {negotiationPoints.map((p: any, idx: number) => (
              <div key={idx} className={styles.changeItem}>
                {/* Accordion Header */}
                <div
                  className={styles.changeItemHeader}
                  onClick={() => toggleChangeItem(idx)}
                  style={{ cursor: "pointer" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <Chip
                      label={`Clause ${p?.affected_clause ?? "-"}`}
                      size="small"
                      sx={{
                        fontSize: "12px",
                        fontWeight: "600",
                        background: "#e0f5f1",
                        color: "#004141",
                        border: "1px solid #b2e5de",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2a312b",
                        flex: 1,
                      }}
                    >
                      {p?.negotiation_point || "-"}
                    </Typography>
                    {openChangeItems[idx] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </div>

                {/* Accordion Content */}
                {openChangeItems[idx] && (
                  <div className={styles.changeItemContent}>
                    {/* Original Clause */}
                    <Box sx={{ marginBottom: 3 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#004141",
                          marginBottom: 1,
                        }}
                      >
                        Original Clause
                      </Typography>
                      <Box
                        sx={{
                          padding: "12px",
                          background: "#f9fafb",
                          borderRadius: "6px",
                          border: "1px solid #e5e7eb",
                          fontSize: "13px",
                          color: "#403f3f",
                          lineHeight: "1.5",
                        }}
                      >
                        <MarkdownRenderer content={p?.original_text || "-"} />
                      </Box>
                    </Box>

                    {/* Updated Clause */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#004141",
                          }}
                        >
                          Updated Clause
                        </Typography>
                        <Tooltip title="Copy updated clause" arrow>
                          <IconButton
                            size="small"
                            onClick={async () => {
                              await navigator.clipboard.writeText(p?.updated_text || "");
                            }}
                            sx={{
                              color: "#6b7280",
                              "&:hover": {
                                backgroundColor: "#f0fbf9",
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          padding: "12px",
                          background: "#f0fbf9",
                          borderRadius: "6px",
                          border: "1px solid #b2e5de",
                          fontSize: "13px",
                          color: "#403f3f",
                          lineHeight: "1.5",
                        }}
                      >
                        <MarkdownRenderer content={p?.updated_text || "-"} />
                      </Box>
                    </Box>
                  </div>
                )}
              </div>
            ))}
          </Box>
        </AccordionItem>
      )}

      {/* COMMENTED OUT: Hide Redrafted Contract section from UI */}
      {/* {redraftedContract && (
        <AccordionItem
          title="Redrafted Contract"
          isOpen={openSections.redraftedContract}
          onToggle={() => toggleSection("redraftedContract")}
        >
          <Box sx={{ position: "relative" }}>
            {/* Action Buttons */}
            {/* <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginBottom: 2 }}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={expandAllContractSections}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#006d77",
                    "&:hover": {
                      backgroundColor: "#f0fbf9",
                    },
                  }}
                >
                  Expand All
                </Button>
                <Button
                  size="small"
                  onClick={collapseAllContractSections}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#006d77",
                    "&:hover": {
                      backgroundColor: "#f0fbf9",
                    },
                  }}
                >
                  Collapse All
                </Button>
              </Stack>

              <Tooltip title="Copy entire contract" arrow>
                <IconButton
                  size="small"
                  onClick={async () => {
                    await navigator.clipboard.writeText(redraftedContract);
                  }}
                  sx={{
                    color: "#6b7280",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack> */}

            {/* Contract Subsections */}
            {/* {contractSections.map((section, index) => (
              <div key={section.key} className={styles.contractSubsection}>
                <AccordionItem
                  title={section.title}
                  isOpen={!!openContractSections[section.key]}
                  onToggle={() => toggleContractSection(section.key)}
                >
                  <MarkdownRenderer content={section.content} />
                </AccordionItem>
              </div>
            ))}
          </Box>
        </AccordionItem>
      )} */}

      {/* Remaining Risks Table */}
      {/* {remainingRisks && remainingRisks.length > 0 && (
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#5a2d0c",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Remaining Risks Summary ({remainingRisks.length})
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e5e7eb",
                      fontWeight: "600",
                      color: "#111827",
                      width: "15%",
                    }}
                  >
                    Risk Level
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e5e7eb",
                      fontWeight: "600",
                      color: "#111827",
                      width: "85%",
                    }}
                  >
                    Clause Content
                  </th>
                </tr>
              </thead>
              <tbody>
                {remainingRisks.map((r: any, idx: number) => {
                  const colors = getSeverityColor(r?.risk_level);
                  return (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #e5e7eb",
                        background: idx % 2 === 0 ? "#fff" : "#f9fafb",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          verticalAlign: "top",
                        }}
                      >
                        <Chip
                          label={r?.risk_level || "Unknown"}
                          size="small"
                          sx={{
                            fontSize: "12px",
                            fontWeight: "600",
                            background: colors.bg,
                            color: colors.text,
                          }}
                        />
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          verticalAlign: "top",
                          color: "#374151",
                          lineHeight: "1.6",
                        }}
                      >
                        {r?.clause_content || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )} */}
    </Box>
  );
}
